import React, { useEffect, useState } from "react";
import {
  Users,
  UserCheck,
  UserX,
  LogOut,
  Filter,
  Menu,
  Home,
  Shield,
  Gift,
  HelpCircle,
  Package,
  FileText,
  MessageCircle,
  Settings,
  Bell,
  ChevronRight,
  ChevronDown,
  Building2,
  Database,
  LucideIcon,
  Activity,
  Gauge,
  Tag,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "./ui/sidebar";
import { Separator } from "./ui/separator";
import { Users as UsersComponent } from "./Users";
import { Roles } from "./Roles";
import { Reports } from "./Reports";
import { ApiResponse, PaginationInfo } from "../types/common";
import { userPlansApi, ApiError, usersApi } from "../utils/api";
import { ApplicationSettings } from "./ApplicationSettings";
import { CoachNotificationSettings } from "./CoachNotificationSettings";
import { AthleteNotificationSettings } from "./AthleteNotificationSettings";
import { PaymentSettings } from "./PaymentSettings";
import { UserQueries } from "./UserQueries";
import { Packages } from "./Packages";
import { PromotionalCoupons } from "./PromotionalCoupons";
import { CouponsTab } from "./PlatformBilling";
import { ChatMessages } from "./ChatMessages";
import { MailSender } from "./MailSender";
import { Organizations } from "./Organizations";
import { StaticDataManager } from "./StaticDataManager";
import { CouponDetails } from "./CouponDetails";
import { toast } from "sonner";

interface DashboardProps {
  onLogout: () => void;
}

interface Stats {
  title: string;
  value: string;
  description: string;
  icon: LucideIcon;
}

interface RecentActivityItem {
  action: string;
  user: string;
  time: string;
}

interface SystemHealth {
  apiResponseMs: number;
  dbStatus: "connected" | "connecting" | "disconnected" | "unknown";
  serverUptimeSeconds: number;
}

type MenuItem = {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
  submenu?: MenuItem[];
};

const menuItems: MenuItem[] = [
  {
    id: "overview",
    title: "Overview",
    icon: Home,
  },
  {
    id: "users",
    title: "User Management",
    icon: Users,
    // badge: 1247,
    submenu: [
      { id: "all-users", title: "All Users", icon: Users },
      // { id: "coaches", title: "Coaches", icon: UserCheck },
      // { id: "athletes", title: "Athletes", icon: UserCheck },
      // { id: "agents", title: "Agents", icon: UserX },
    ],
  },
  {
    id: "roles",
    title: "Roles",
    icon: Shield,
  },
  {
    id: "packages",
    title: "Packages",
    icon: Package,
  },
  {
    id: "chat",
    title: "Chat Messages",
    icon: MessageCircle,
    // badge: 23,
  },
  {
    id: "coupons",
    title: "Promotional Coupons",
    icon: Gift,
  },
  {
    id: "coupon-management",
    title: "Organization Coupons",
    icon: Tag,
  },
  {
    id: "reports",
    title: "Reports",
    icon: FileText,
  },
  {
    id: "queries",
    title: "User Queries",
    icon: HelpCircle,
    // badge: 7,
  },
  {
    id: "platform-data",
    title: "Platform Data",
    icon: Database,
  },
  {
    id: "organizations",
    title: "Organizations",
    icon: Building2,
  },
  {
    id: "mail",
    title: "Bulk Mail Sender",
    icon: FileText,
  },
  {
    id: "settings",
    title: "Settings",
    icon: Settings,
    submenu: [
      { id: "app-settings", title: "Application Settings", icon: Settings },
      { id: "coach-notifications", title: "Coach Notifications", icon: Bell },
      {
        id: "athlete-notifications",
        title: "Athlete Notifications",
        icon: Bell,
      },
      { id: "payment-settings", title: "Payment Settings", icon: Settings },
    ],
  },
];

export function Dashboard({ onLogout }: DashboardProps) {
  // Read tab from URL hash (e.g. #users), fallback to overview
  const getTabFromHash = () => {
    if (typeof window !== "undefined" && window.location.hash) {
      return window.location.hash.replace(/^#/, "");
    }
    return "overview";
  };
  const [activeTab, setActiveTab] = useState(getTabFromHash());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [stats, setStats] = useState<Stats[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivityItem[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  // Sync tab to URL hash when changed
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window.location.hash.replace(/^#/, "") !== activeTab) {
        window.location.hash = activeTab;
      }
    }
  }, [activeTab]);

  // On mount, listen for hash changes (browser navigation)
  useEffect(() => {
    const onHashChange = () => {
      const tab = window.location.hash.replace(/^#/, "");
      setActiveTab(tab || "overview");
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Initial load
  useEffect(() => {
    loadStatistics();
  }, []);

  // Load stats
  const loadStatistics = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = {
        page,
        limit: 10,
      };

      const response: ApiResponse = await usersApi.getUsersStatistics(params);
      
      if (response.success) {
        setStats(response.data.stats || []);
        setRecentActivity(response.data.recentActivity || []);
        setSystemHealth(response.data.systemHealth || null);
      } else {
        throw new Error(response.message || 'Failed to load user stats');
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? `${err.message} (${err.status})` 
        : 'Failed to load user stats';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    window.location.hash = "overview";
    onLogout();
  };

  const getActiveComponent = () => {
    if (activeTab === "coupon-management" || activeTab.startsWith("coupon-management/")) {
      if (activeTab === "coupon-management") {
        return <CouponsTab />;
      }

      const rest = activeTab.replace("coupon-management/", "");
      const [first, second] = rest.split("/");

      if (first === "new") {
        return (
          <CouponDetails
            couponId={null}
            mode="create"
            kind={second === "free-grant" ? "free-grant" : "discount"}
            onBack={() => setActiveTab("coupon-management")}
          />
        );
      }

      return (
        <CouponDetails
          couponId={first}
          mode={second === "edit" ? "edit" : "view"}
          onBack={() => setActiveTab("coupon-management")}
        />
      );
    }

    switch (activeTab) {
      case "overview":
        return (
          <OverviewContent
            searchTerm={searchTerm}
            filterRole={filterRole}
            stats={stats}
            recentActivity={recentActivity}
            systemHealth={systemHealth}
          />
        );
      case "all-users":
      case "users":
        return (
          <UsersComponent searchTerm={searchTerm} filterRole={filterRole} />
        );
      case "roles":
        return <Roles />;
      case "packages":
        return <Packages />;
      case "chat":
        return <ChatMessages />;
      case "coupons":
        return <PromotionalCoupons />;
      case "reports":
        return <Reports />;
      case "queries":
        return <UserQueries />;
      case "platform-data":
        return <StaticDataManager />;
      case "organizations":
        return <Organizations />;
      case "mail":
        return <MailSender />;
      case "app-settings":
        return <ApplicationSettings />;
      case "coach-notifications":
        return <CoachNotificationSettings />;
      case "athlete-notifications":
        return <AthleteNotificationSettings />;
      case "payment-settings":
        return <PaymentSettings />;
      default:
        // If hash is unknown, fallback to overview
        return (
          <OverviewContent
            searchTerm={searchTerm}
            filterRole={filterRole}
            stats={stats}
            recentActivity={recentActivity}
            systemHealth={systemHealth}
          />
        );
    }
  };

  const renderMenuItem = (item: MenuItem, depth: number = 0) => {
    const isExpanded = expandedItems.includes(item.id);
    const isActive = activeTab === item.id;
    const hasSubmenu = item.submenu && item.submenu.length > 0;

    return (
      <div key={item.id}>
        <SidebarMenuButton
          className={`w-full justify-between ${
            isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""
          }`}
          style={{ paddingLeft: `${(depth + 1) * 12}px` }}
          onClick={() => {
            if (hasSubmenu) {
              toggleExpanded(item.id);
            } else {
              setActiveTab(item.id);
              // Set hash for direct navigation
              if (typeof window !== "undefined") {
                window.location.hash = item.id;
              }
            }
          }}
        >
          <div className="flex items-center gap-2">
            <item.icon className="h-4 w-4" />
            <span className="flex-1 text-left">{item.title}</span>
          </div>
          <div className="flex items-center gap-1">
            {item.badge && (
              <Badge variant="secondary" className="h-5 text-xs">
                {item.badge}
              </Badge>
            )}
            {hasSubmenu && (
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  isExpanded ? "rotate-180" : ""
                }`}
              />
            )}
          </div>
        </SidebarMenuButton>

        {hasSubmenu && isExpanded && item.submenu && (
          <div className="mt-1">
            {item.submenu.map((subItem) => renderMenuItem(subItem, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex min-h-screen w-full">
      <Sidebar className="border-r">
        <SidebarHeader className="border-b p-4 bg-gradient-to-br from-blue-50 via-white to-purple-50">
          <div className="flex items-center gap-2">
            <img
              src="https://admin.coachable-app.com/images/logo.jpeg"
              alt="Coachable"
              className="h-9 w-auto shrink-0 rounded-md object-contain"
            />
            <div>
              <h2 className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Coachable Admin
              </h2>
              <p className="text-xs text-muted-foreground">Dashboard v2.0</p>
            </div>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarMenu className="p-2">
            {menuItems.map((item) => (
              <SidebarMenuItem key={item.id}>
                {renderMenuItem(item)}
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarContent>

        <SidebarFooter className="border-t p-4">
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </SidebarFooter>
      </Sidebar>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-14 items-center gap-4 px-4 lg:px-6">
            <SidebarTrigger />
            <div className="flex-1" />
            {(activeTab === "all-users" ||
              activeTab === "users" ||
              activeTab === "overview") && (
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Input
                    placeholder="Search users..."
                    className="w-28 sm:w-48 lg:w-64"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-24 sm:w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="COACH">Coaches</SelectItem>
                    <SelectItem value="ATHLETE">Athletes</SelectItem>
                    <SelectItem value="AGENT">Agents</SelectItem>
                    <SelectItem value="ORGANIZATION">Organizations</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 min-w-0 p-4 lg:p-6">
          {getActiveComponent()}
        </main>
      </div>
    </div>
  );
}

const STAT_CARD_ACCENTS = [
  { badge: "bg-blue-100 text-blue-600", border: "border-l-4 border-l-blue-400" },
  { badge: "bg-emerald-100 text-emerald-600", border: "border-l-4 border-l-emerald-400" },
  { badge: "bg-amber-100 text-amber-600", border: "border-l-4 border-l-amber-400" },
  { badge: "bg-purple-100 text-purple-600", border: "border-l-4 border-l-purple-400" },
];

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function OverviewContent({
  searchTerm,
  filterRole,
  stats,
  recentActivity,
  systemHealth,
}: {
  searchTerm: string;
  filterRole: string;
  stats: any[];
  recentActivity: { action: string; user: string; time: string }[];
  systemHealth: {
    apiResponseMs: number;
    dbStatus: string;
    serverUptimeSeconds: number;
  } | null;
}) {

  const iconMap: Record<string, LucideIcon> = {
    Users,
    UserCheck,
    UserX,
  };

  const uiStats = stats.map(stat => ({
    ...stat,
    icon: iconMap[stat.icon] ?? Users, // fallback to Users
  }));

  const healthMetrics = systemHealth
    ? [
        {
          metric: "API Response Time",
          value: `${systemHealth.apiResponseMs}ms`,
          good: systemHealth.apiResponseMs < 1000,
        },
        {
          metric: "Database",
          value:
            systemHealth.dbStatus.charAt(0).toUpperCase() +
            systemHealth.dbStatus.slice(1),
          good: systemHealth.dbStatus === "connected",
        },
        {
          metric: "Server Uptime",
          value: formatUptime(systemHealth.serverUptimeSeconds),
          good: true,
        },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 text-white shadow-sm">
          <Home className="h-5 w-5" />
        </span>
        <div>
          <h1>Admin Dashboard</h1>
          <p>Manage your Coachable platform from this central hub.</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {uiStats?.map((stat, index) => {
          const accent = STAT_CARD_ACCENTS[index % STAT_CARD_ACCENTS.length];
          return (
            <Card key={index} className={accent.border}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${accent.badge}`}>
                  <stat.icon className="h-4 w-4" />
                </span>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">
                  {stat.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="border-l-4 border-l-sky-400">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                <Activity className="h-4 w-4" />
              </span>
              Recent Activity
            </CardTitle>
            <CardDescription>
              Latest user actions and system events
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recent activity.</p>
            ) : (
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium capitalize">{activity.action}</p>
                      <p className="text-xs text-muted-foreground">
                        {activity.user}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {timeAgo(activity.time)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-teal-400">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-600">
                <Gauge className="h-4 w-4" />
              </span>
              System Health
            </CardTitle>
            <CardDescription>Platform performance and status</CardDescription>
          </CardHeader>
          <CardContent>
            {healthMetrics.length === 0 ? (
              <p className="text-sm text-muted-foreground">Health data unavailable.</p>
            ) : (
              <div className="space-y-4">
                {healthMetrics.map((metric, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm">{metric.metric}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{metric.value}</span>
                      <div
                        className={`h-2 w-2 rounded-full ${
                          metric.good ? "bg-green-500" : "bg-red-500"
                        }`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
